import { useCallback, useEffect, useRef, useState } from "react";
import { LogOut } from "lucide-react";
import { Link } from "react-router-dom";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Brand } from "@/pages/Login";
import { useAuth } from "@/lib/auth";
import { api, setLedgerMode } from "@/lib/api";
import { StatsRow } from "@/components/doorman/StatsRow";
import { LedgerPanel } from "@/components/doorman/LedgerPanel";
import { DetectionPanel } from "@/components/doorman/DetectionPanel";
import { AnchorsPanel } from "@/components/doorman/AnchorsPanel";
import { DevicesPanel } from "@/components/doorman/DevicesPanel";
import { AccountsPanel } from "@/components/doorman/AccountsPanel";
import { SimulatorPanel } from "@/components/doorman/SimulatorPanel";
import { BrandMark } from "@/components/onboarding/BrandMark";
import { MspHome } from "@/components/onboarding/MspHome";
import { MspsPanel } from "@/components/onboarding/MspsPanel";
import { AdminProperties } from "@/components/onboarding/AdminProperties";
import { PaymentDue } from "@/components/onboarding/PaymentDue";
import { DisclosurePanel } from "@/components/onboarding/DisclosurePanel";
import { OwnerHome } from "@/components/onboarding/OwnerHome";
import { IntegrationsPanel } from "@/components/onboarding/IntegrationsPanel";
import { useBrandTitle } from "@/lib/useBrandTitle";

export default function Dashboard() {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [tick, setTick] = useState(0);
  const [mode, setMode] = useState("live");
  const [tab, setTab] = useState("ledger");
  const tabsRef = useRef(null);
  const isAdmin = user?.role === "admin";
  const openTab = (t) => {
    if (!t) return setTick((x) => x + 1);
    setTab(t);
    setTimeout(() => {
      const el = document.querySelector('[role="tabpanel"][data-state="active"]') || tabsRef.current;
      if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - 88 });
    }, 300);
  };
  const sandbox = mode === "sandbox";
  const isMsp = user?.role === "msp";
  const unpaid = user?.billing_status && user.billing_status !== "active";
  const gated = isMsp || unpaid;
  useBrandTitle(user?.brand);

  const refresh = useCallback(() => setTick((t) => t + 1), []);
  const switchMode = useCallback((m) => {
    setLedgerMode(m);
    setMode(m);
    if (m === "sandbox") setTab((t) => (["devices", "detection", "integrations"].includes(t) ? "ledger" : t));
    setTick((t) => t + 1);
  }, []);

  useEffect(() => () => setLedgerMode("live"), []);

  useEffect(() => {
    api.get("/stats").then((r) => setStats(r.data)).catch(() => {});
  }, [tick]);

  return (
    <div className="min-h-screen bg-[#07090E]">
      <header className="sticky top-0 z-40 backdrop-blur-md bg-[#07090E]/90 border-b border-[#222D3D]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-3">
          <Link to={user?.brand ? "/app" : "/"} data-testid="dashboard-home-link">{user?.brand ? <BrandMark brand={user.brand} compact /> : <Brand compact />}</Link>
          <div className="flex items-center gap-3">
            {!gated && <ModeSwitch mode={mode} onChange={switchMode} />}
            <span className="hidden lg:inline font-mono text-xs text-slate-500" data-testid="header-user-email">{user?.company ? `${user.company} · ` : ""}{user?.email}</span>
            <Button variant="ghost" size="sm" onClick={logout} data-testid="logout-btn" className="text-slate-400 hover:text-slate-100">
              <LogOut className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Sign out</span>
            </Button>
          </div>
        </div>
        {sandbox && (
          <div className="bg-amber-500/10 border-t border-amber-500/30 text-amber-200 text-xs text-center py-1.5 font-mono" data-testid="sandbox-banner">
            Simulation ledger — simulated traffic only, kept apart from your live ledger
          </div>
        )}
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-10">
        {isMsp ? <MspView /> : unpaid ? <PaymentDue user={user} /> : <>
        {isAdmin && !sandbox && <OwnerHome key={tick} onOpen={openTab} />}
        <div className="fade-up">
          <p className={`font-mono text-xs tracking-[0.3em] uppercase ${sandbox ? "text-amber-400" : "text-emerald-400"}`}>{sandbox ? "Simulation" : "Arrivals"}</p>
          <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-slate-100 mt-2">Agent inventory</h1>
          <p className="text-slate-400 text-sm mt-2 max-w-xl">Who arrived, and when. Chained entry by entry and anchored to Bitcoin.</p>
        </div>

        <StatsRow stats={stats} />

        <Tabs value={tab} onValueChange={setTab} className="space-y-6 scroll-mt-24" ref={tabsRef}>
          <div>
            <TabsList className="bg-[#0F141C] border border-[#222D3D] h-auto flex flex-wrap justify-start gap-1 p-1">
              {user?.role === "admin" && <TabsTrigger value="accounts" data-testid="tab-accounts">Accounts</TabsTrigger>}
              {user?.role === "admin" && <TabsTrigger value="msps" data-testid="tab-msps">MSPs</TabsTrigger>}
              {user?.role === "admin" && <TabsTrigger value="properties" data-testid="tab-properties">Properties</TabsTrigger>}
              {user?.role === "admin" && <TabsTrigger value="disclosure" data-testid="tab-disclosure">IP record</TabsTrigger>}
              <TabsTrigger value="ledger" data-testid="tab-ledger">Ledger</TabsTrigger>
              <TabsTrigger value="simulator" data-testid="tab-simulator">Simulator</TabsTrigger>
              {!sandbox && <TabsTrigger value="devices" data-testid="tab-devices">Devices</TabsTrigger>}
              <TabsTrigger value="anchors" data-testid="tab-anchors">Bitcoin anchors</TabsTrigger>
              {!sandbox && <TabsTrigger value="detection" data-testid="tab-detection">Web snippet</TabsTrigger>}
              {!sandbox && <TabsTrigger value="integrations" data-testid="tab-integrations">Software access</TabsTrigger>}
            </TabsList>
          </div>
          {user?.role === "admin" && <TabsContent value="accounts"><AccountsPanel /></TabsContent>}
          {user?.role === "admin" && <TabsContent value="msps"><MspsPanel /></TabsContent>}
          {user?.role === "admin" && <TabsContent value="properties"><AdminProperties /></TabsContent>}
          {user?.role === "admin" && <TabsContent value="disclosure"><DisclosurePanel /></TabsContent>}
          <TabsContent value="ledger"><LedgerPanel tick={tick} onChange={refresh} /></TabsContent>
          <TabsContent value="simulator"><SimulatorPanel tick={tick} onChange={refresh} onViewSandbox={() => switchMode("sandbox")} sandbox={sandbox} /></TabsContent>
          {!sandbox && <TabsContent value="devices"><DevicesPanel tick={tick} onChange={refresh} /></TabsContent>}
          <TabsContent value="anchors"><AnchorsPanel tick={tick} pending={stats?.unanchored ?? 0} onChange={refresh} /></TabsContent>
          {!sandbox && <TabsContent value="detection"><DetectionPanel onChange={refresh} /></TabsContent>}
          {!sandbox && <TabsContent value="integrations"><IntegrationsPanel /></TabsContent>}
        </Tabs>
        </>}
      </main>
    </div>
  );
}

const MspView = () => (
  <>
    <div className="fade-up">
      <p className="font-mono text-xs tracking-[0.3em] uppercase text-emerald-400">MSP partner</p>
      <h1 className="text-3xl sm:text-4xl font-semibold tracking-tight text-slate-100 mt-2">Partner portal</h1>
      <p className="text-slate-400 text-sm mt-2 max-w-xl">Share your branded sign-up page, follow each property's payment and mark installs complete.</p>
    </div>
    <MspHome />
  </>
);

const ModeSwitch = ({ mode, onChange }) => (
  <div className="flex rounded-md border border-[#222D3D] bg-[#0F141C] p-0.5 text-xs" data-testid="ledger-mode-switch">
    {[["live", "Live"], ["sandbox", "Simulation"]].map(([m, label]) => (
      <button key={m} onClick={() => onChange(m)} data-testid={`ledger-mode-${m}`}
        className={`px-3 py-1.5 rounded transition-colors ${mode === m ? (m === "live" ? "bg-emerald-500 text-[#07090E] font-semibold" : "bg-amber-500 text-[#07090E] font-semibold") : "text-slate-400 hover:text-slate-100"}`}>
        {label}
      </button>
    ))}
  </div>
);
